/**
 * PendingDevicesContext - Tracks pending devices needing approval
 *
 * Subscribes to update poller and maintains list of pending devices.
 * Used by DeviceSyncStatus to show badge count.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUpdatePoller } from '@yada/sync';
import type { Device } from '../accounts/deviceService';
import { createLogger } from '@yada/logger';

const logger = createLogger('PendingDevicesContext');

interface PendingDevicesContextValue {
  pendingDevices: Device[];
  pendingCount: number;
  refresh: () => void;
}

const PendingDevicesContext = createContext<PendingDevicesContextValue>({
  pendingDevices: [],
  pendingCount: 0,
  refresh: () => {},
});

export const usePendingDevices = () => useContext(PendingDevicesContext);

interface PendingDevicesProviderProps {
  children: React.ReactNode;
}

export const PendingDevicesProvider: React.FC<PendingDevicesProviderProps> = ({
  children,
}) => {
  const [pendingDevices, setPendingDevices] = useState<Device[]>([]);

  const refresh = useCallback(() => {
    // Trigger immediate poll
    getUpdatePoller().pollNow();
  }, []);

  useEffect(() => {
    const poller = getUpdatePoller();

    // Subscribe to pending device updates
    const unsubscribe = poller.on('devices:pending', (devices: Device[]) => {
      logger.log('Received pending devices:', devices);
      setPendingDevices(devices);
    });

    // Start polling (idempotent)
    poller.start();

    // Trigger immediate poll on mount
    poller.pollNow();

    const handleAccountChanged = () => {
      if (localStorage.getItem('yada:access_token')) {
        poller.start();
        poller.pollNow();
      }
    };
    window.addEventListener('yada:account:changed', handleAccountChanged);

    return () => {
      unsubscribe();
      window.removeEventListener('yada:account:changed', handleAccountChanged);
      // Note: Don't stop poller here — it might be used by other components
    };
  }, []);

  const value: PendingDevicesContextValue = {
    pendingDevices,
    pendingCount: pendingDevices.length,
    refresh,
  };

  return (
    <PendingDevicesContext.Provider value={value}>
      {children}
    </PendingDevicesContext.Provider>
  );
};
